import { ReportType, ShapeType, FilterType } from '../../reportsTypes';
import { ReportCloneValues } from './createReportCloneFormSchema';
import { ShapeDictionaryType, ReportFilterType } from '../../utils';
import { extractNamesFromShape } from './extractNames';
import { extractMatchingData } from './extractMatchingData';
import {
  createShapeDictionary,
  createPortDictionary,
  createWorldDictionary,
} from './createShapeDictionary';
import { createFilter } from './createFilter';

type ShapeDictionaryInput = Parameters<typeof createShapeDictionary>[0];
export type ShapeCollection = Parameters<typeof createShapeDictionary>[1];
type PortDictionaryInput = Parameters<typeof createPortDictionary>[0];
export type PortCollection = Parameters<typeof createPortDictionary>[1];
export type FilterCollection = Parameters<typeof createFilter>[1];

interface ShapeDictionaryArgs {
  values: Partial<ReportCloneValues>;
  reportType: ReportType;
  shapeType: ShapeType;
  shapes?: ShapeCollection;
  ports?: PortCollection;
  instantShape?: boolean;
}

interface FilterArgs {
  values: Partial<ReportCloneValues>;
  reportType: ReportType;
  filterType: FilterType;
  filters?: FilterCollection;
}

const hasShapeFieldData = (data: Record<string, unknown>) => {
  return Object.values(data).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'boolean') return value;
    return value != null;
  });
};

export const deriveShapeDictionaryFromValues = ({
  values,
  reportType,
  shapeType,
  shapes,
  ports,
  instantShape = false,
}: ShapeDictionaryArgs): ShapeDictionaryType => {
  if (!reportType.shape) {
    return null;
  }

  const shapeNames = extractNamesFromShape(reportType.shape, shapeType) || [];
  
  if (shapeNames.length === 0) {
    return null;
  }

  const shapeData = extractMatchingData(values, shapeNames);
  
  if (!hasShapeFieldData(shapeData)) {
    return null;
  }

  if (shapeType === 'ports') {
    if (!ports) return null;
    const dictionary = createPortDictionary(
      shapeData as PortDictionaryInput,
      ports
    );
    return Object.keys(dictionary).length ? dictionary : null;
  }

  if (shapeType === 'worldShape') {
    const worldData: Record<string, boolean> = {};
    Object.keys(shapeData).forEach((key) => {
      const value = shapeData[key];
      if (typeof value === 'boolean') {
        worldData[key] = value;
      } else if (value != null) {
        worldData[key] = Boolean(value);
      }
    });
    const dictionary = createWorldDictionary(worldData);
    return Object.keys(dictionary).length ? dictionary : null;
  }

  if (!shapes || shapes.length === 0) {
    return null;
  }
  
  const dictionary = createShapeDictionary(
    shapeData as ShapeDictionaryInput,
    shapes,
    instantShape
  );
  
  return Object.keys(dictionary).length ? dictionary : null;
};

const hasFilterFieldData = (values: Partial<ReportCloneValues>) => {
  const valueRecord = values as Record<string, unknown>;
  const rawFilter = valueRecord.filter;
  const shiplist = valueRecord.shiplist;

  if (Array.isArray(rawFilter)) return rawFilter.length > 0;
  if (typeof rawFilter === 'string' && rawFilter.trim() !== '') return true;
  if (rawFilter && typeof rawFilter === 'object') return true;
  if (typeof shiplist === 'string' && shiplist.trim() !== '') return true;
  return false;
};

export const deriveFilterFromValues = ({
  values,
  reportType,
  filterType,
  filters,
}: FilterArgs): ReportFilterType | null => {
  if (!reportType.filter) return null;
  if (!hasFilterFieldData(values)) return null;

  const availableFilters = filters ?? ([] as FilterCollection);
  return createFilter(values, availableFilters, filterType);
};
